import { availabilityApi, contentApi } from '../../infrastructure/Configuration'
import Hotel from '../domain/Hotel'
import NewMapper from './NewMapper'
import ExternalStorage from '@/services/ExternalStorage'
import { hotelStayDatesFactory } from '../infrastructure/Factory'
import { DateFactory } from 'rhg-vue-common'
import { redirect } from '../../infrastructure/Utils'

export default class NewRdHotelService {
  constructor(httpClient) {
    this.httpClient = httpClient
    this.defaultCurrency = 'EUR'
  }

  async getHotel (hotelCode) {
    const url = `${contentApi}/hotels/${hotelCode}`
    const { data: { hotel } } = await this.httpClient.get(url)
    return hotel
  }

  async getRooms (hotelCode) {
    const url = `${contentApi}/hotels/${hotelCode}/rooms`
    const { data: { rooms } } = await this.httpClient.get(url)
    return rooms
  }

  async getStays (customer, jsonHotel, jsonRooms, queryParams, taxIncludedRegion, taxExcludedRegion, preselectedFilters) {
    const { checkInDate, checkOutDate } = hotelStayDatesFactory.create(queryParams)
    const rooms = (queryParams.adults && queryParams.adults.length) || 1// 1
    const adults = (queryParams.adults && queryParams.adults.reduce((accumulation, adults) => accumulation >= parseInt(adults) ? accumulation : parseInt(adults), 0)) || 2
    const children = (queryParams.children && queryParams.children.reduce((accumulation, children) => accumulation >= parseInt(children) ? accumulation : parseInt(children), 0)) || 0

    queryParams = {
      ...queryParams,
      checkInDate: DateFactory.formatDate(DateFactory.dateObject(checkInDate)),
      checkOutDate: DateFactory.formatDate(DateFactory.dateObject(checkOutDate)),
      rooms,
      adults,
      children,
      taxIncludedRegion: (taxIncludedRegion ? true : undefined) || (taxExcludedRegion ? false : undefined),
      roomIds: [...new Set(jsonRooms.reduce((acc, currRoom) => currRoom.tmsCode ? [...acc, currRoom.tmsCode, currRoom.id] : [...acc, currRoom.id], []))],
      sourceOfBusiness: ExternalStorage.global.sourceOfBusiness(),
      voucherCode: queryParams.voucher || undefined
    }

    if (customer.isLogged) {
      const isTier = !!(jsonHotel.tierDiscount && queryParams.searchType === 'lowest' /* SearchTypes.LOWEST */ && customer.tier)
      queryParams = isTier
        ? {
          ...queryParams,
          tierDiscount: {
            tierType: customer.tier
          }
        }
        : { ...queryParams }
    }

    const transactionId = ExternalStorage.session.transactionId()
    const saverHeader = (localStorage.getItem('saver') || 'true') === 'true'
    const preferredCurrency = global.Cookie.read(window.cookieDictionary.preferredCurrency)

    const config = {
      headers: {
        Saver: saverHeader,
        transactionId,
        'X-Currency': preferredCurrency || 'EUR'
      }
    }

    !transactionId && delete config.headers.transactionId
    !saverHeader && delete config.headers.Saver
    delete queryParams.voucher

    try {
      return await this.getStaysByRooms(queryParams, config, jsonHotel, jsonRooms, preselectedFilters, customer)
    } catch (error) {
      if (error && error.response && error.response.status === 428) {
        const timeoutError428 = global.ConfigDynamic.valueOf({ key: 'error.428.timeout', defaultValue: '0' })
        if (timeoutError428 > 0) {
          return await new Promise((resolve, reject) => {
            setTimeout(() => { redirect(window.location.href, {}) }, timeoutError428)
          })
        }
      } else if (error.response.status === 500) {
        global.ModalMessage
          .modal('#modal-error')
          .show()
        return Promise.resolve(error)
      }
      return Promise.reject(error)
    }
  }

  async getStaysByRooms (queryParams, config, jsonHotel, jsonRooms, preselectedFilters, customer) {
    const paramsToSend = { ...queryParams, ...customer }
    const response = await this.httpClient.post(`${availabilityApi}/stays-by-rooms`, paramsToSend, config)

    const json = response.data

    ExternalStorage.session.saveTravelAgencyCustomerId(json.customerId)
    ExternalStorage.session.savePromotionalCodeGroupId(json.groupId)

    return NewMapper
      .fromJson({
        jsonHotel: jsonHotel,
        jsonRooms: jsonRooms,
        jsonStaysByRooms: json.rooms,
        minNightsPerStay: json.minNightsPerStay,
        hotelCurrency: json.currency,
        payUponArrival: json.payUponArrival,
        searchApplicable: json.searchApplicable,
        searchType: queryParams.searchType,
        preselectedFilters
      })
      .toDomain()
  }

  async getCurrency (currency) {
    const exchangeRatesStorage = global.UtilsCurrency.getCurrenciesInSessionStorage(currency)
    let exchangeRatesList = []
    if (!exchangeRatesStorage || exchangeRatesStorage.length === 0) {
      const url = `${availabilityApi}/exchange-rates/${this.defaultCurrency}`
      const { data: { exchangeRates } } = await this.httpClient.get(url)
      global.UtilsCurrency.setCurrenciesInSessionStorage(exchangeRates)
      exchangeRatesList = exchangeRates
    } else {
      exchangeRatesList = exchangeRatesStorage
    }
    return exchangeRatesList
  }

  async getNearbyHotels (hotelCode) {
    const preferredCurrency = global.Cookie.read(window.cookieDictionary.preferredCurrency)

    const config = {
      headers: {
        'X-Currency': preferredCurrency || 'EUR'
      }
    }

    const url = `${contentApi}/hotels/${hotelCode}/nearby-hotels`
    const { data: { nearbyHotels } } = await this.httpClient.get(url, config)
    return (nearbyHotels || [])
      .map(it => Hotel.fromJSON(it))
  }

  async getCoBrandImage (cobrandKey) {
    const params = {
      coBrandKeys: cobrandKey.join(',')
    }
    const url = `${contentApi}/hotels/cobrands/images`
    const coBrand = await this.httpClient.get(url, { params })
    return coBrand.data
  }
}
